import {
  formatAge,
  type BrokerStatus,
  type Stats,
  type Summary,
} from '@mcoverage/shared';
import type { FiltersState } from '../lib/api';
import { ChevronIcon, CloseIcon } from './icons';

interface SidebarProps {
  id: string;
  stats: Stats | null;
  summary: Summary | null;
  filters: FiltersState;
  onChange: (next: FiltersState) => void;
  onReset: () => void;
  open: boolean;
  onToggle: () => void;
  activeCount: number;
}

/**
 * The time window is a single concept but two filter flags, so the options are
 * modelled as explicit keys rather than as raw hours.
 */
const TIME_WINDOWS: { key: string; label: string; onlineOnly: boolean; hours: number }[] = [
  { key: 'live', label: 'Live (15 min)', onlineOnly: true, hours: 0 },
  { key: '24h', label: '24 Stunden', onlineOnly: false, hours: 24 },
  { key: '7d', label: '7 Tage', onlineOnly: false, hours: 24 * 7 },
  { key: '30d', label: '30 Tage', onlineOnly: false, hours: 24 * 30 },
  { key: 'all', label: 'Allzeit', onlineOnly: false, hours: 0 },
];

function windowKey(filters: FiltersState): string {
  const match = TIME_WINDOWS.find(
    (w) => w.onlineOnly === filters.onlineOnly && w.hours === filters.seenWithinHours,
  );
  return match?.key ?? 'all';
}

function BucketList({
  title,
  buckets,
  selected,
  onToggle,
  limit = 8,
  formatKey = (k: string) => k,
}: {
  title: string;
  buckets: { key: string; label: string; count: number }[];
  selected: string[];
  onToggle: (key: string) => void;
  limit?: number;
  formatKey?: (key: string) => string;
}) {
  if (buckets.length === 0) return null;
  const shown = buckets.slice(0, limit);
  const max = Math.max(...shown.map((b) => b.count));

  return (
    <div className="facet">
      <h3 className="section-title">{title}</h3>
      <ul>
        {shown.map((b) => {
          const active = selected.includes(formatKey(b.key));
          return (
            <li key={b.key}>
              <button
                type="button"
                className={active ? 'facet-row active' : 'facet-row'}
                onClick={() => onToggle(b.key)}
                aria-pressed={active}
                title={`${b.label}: ${b.count} Nodes`}
              >
                <span className="facet-bar" style={{ width: `${(b.count / max) * 100}%` }} />
                <span className="facet-label">{b.label}</span>
                <span className="facet-count">{b.count.toLocaleString('de-DE')}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A broker can accept the login and still deliver nothing -- typically because
 * the account is not allowed to read the `msh/` topic tree. That looks exactly
 * like success if you only report the connection state, so it gets its own
 * warning state.
 */
function brokerView(broker: BrokerStatus): { state: string; cls: string; hint?: string } {
  if (!broker.enabled) return { state: 'Aus', cls: 'off' };
  if (!broker.connected) {
    return broker.lastError
      ? { state: 'Fehler', cls: 'bad', hint: broker.lastError }
      : { state: 'Verbinde…', cls: 'warn' };
  }
  if (broker.messages === 0) {
    return {
      state: 'Keine Daten',
      cls: 'warn',
      hint: 'Verbunden, aber der Broker liefert keine Pakete. Häufigste Ursache: das Konto darf den Topic-Baum msh/ nicht lesen.',
    };
  }
  return {
    state: `${broker.messages.toLocaleString('de-DE')} Msg`,
    cls: 'on',
    hint: broker.lastMessageAt
      ? `Letzte Nachricht ${formatAge(Date.now() / 1000 - broker.lastMessageAt / 1000)}`
      : undefined,
  };
}

function BrokerRow({ broker }: { broker: BrokerStatus }) {
  const { state, cls, hint } = brokerView(broker);
  return (
    <li className="broker" title={hint ?? broker.url}>
      <span className={`dot ${cls}`} aria-hidden />
      <span className="broker-name">{broker.label}</span>
      <span className="broker-state">{state}</span>
    </li>
  );
}

/** Adds the value if absent, removes it if present. */
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function Sidebar({
  id,
  stats,
  summary,
  filters,
  onChange,
  onReset,
  open,
  onToggle,
  activeCount,
}: SidebarProps) {
  const set = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) =>
    onChange({ ...filters, [key]: value });

  const toggleIn = (key: 'hardware' | 'roles' | 'regions', value: string) => {
    if (key === 'regions') {
      onChange({ ...filters, regions: toggle(filters.regions, value) });
    } else if (key === 'hardware') {
      onChange({ ...filters, hardware: toggle(filters.hardware, Number(value)) });
    } else {
      onChange({ ...filters, roles: toggle(filters.roles, Number(value)) });
    }
  };

  const brokers = stats?.brokers ?? [];

  return (
    <aside id={id} className={open ? 'sheet open' : 'sheet'} aria-label="Filter">
      <div className="sheet-head">
        <h2>Filter</h2>
        {activeCount > 0 && <span className="chip">{activeCount} aktiv</span>}
        <button type="button" className="link-btn" onClick={onReset}>
          Zurücksetzen
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onToggle}
          aria-label="Filter schließen"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="sheet-body">
        <section>
          <h3 className="section-title">Auswahl</h3>

          <label className="field">
            <span>Zeitraum</span>
            <select
              value={windowKey(filters)}
              onChange={(e) => {
                const w = TIME_WINDOWS.find((x) => x.key === e.target.value);
                if (!w) return;
                onChange({ ...filters, onlineOnly: w.onlineOnly, seenWithinHours: w.hours });
              }}
            >
              {TIME_WINDOWS.map((w) => (
                <option key={w.key} value={w.key}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Mindestbatterie</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={filters.batteryMin}
              onChange={(e) => set('batteryMin', Number(e.target.value))}
            />
            <em>{filters.batteryMin === 0 ? 'egal' : `≥ ${filters.batteryMin}%`}</em>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={filters.onlineOnly}
              onChange={(e) => onChange({ ...filters, onlineOnly: e.target.checked })}
            />
            <span>Nur gerade online</span>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={filters.includeHidden}
              onChange={(e) => onChange({ ...filters, includeHidden: e.target.checked })}
            />
            <span>Versteckte Positionen zeigen</span>
          </label>
        </section>

        <div className="divider" />

        <section>
          <BucketList
            title="Hardware"
            buckets={summary?.hardware ?? []}
            selected={filters.hardware.map(String)}
            onToggle={(k) => toggleIn('hardware', k)}
          />
          <BucketList
            title="Rolle"
            buckets={summary?.roles ?? []}
            selected={filters.roles.map(String)}
            onToggle={(k) => toggleIn('roles', k)}
          />
          <BucketList
            title="Region"
            buckets={summary?.regions ?? []}
            selected={filters.regions}
            onToggle={(k) => toggleIn('regions', k)}
            formatKey={(k) => k.toUpperCase()}
          />
          {summary &&
            summary.hardware.length === 0 &&
            summary.roles.length === 0 &&
            summary.regions.length === 0 && (
              <p className="empty">Im Ausschnitt liegen keine Nodes.</p>
            )}
        </section>

        <div className="divider" />

        <section>
          <h3 className="section-title">Broker</h3>
          {brokers.length === 0 ? (
            <p className="empty">Keine Broker konfiguriert.</p>
          ) : (
            <ul className="broker-list">
              {brokers.map((b) => (
                <BrokerRow key={b.id} broker={b} />
              ))}
            </ul>
          )}
          {stats?.lastIngestAt && (
            <p className="hint">
              Letzte Daten: {formatAge(Date.now() / 1000 - stats.lastIngestAt / 1000)}
            </p>
          )}
        </section>

        <details className="notes">
          <summary>
            Hinweise
            <ChevronIcon />
          </summary>
          <p className="hint">
            Ein Node erscheint sofort mit seiner Position, aber sein <strong>Name</strong> kommt
            erst mit der <strong>NodeInfo</strong> des Geräts. Meshtastic sendet die nur in langen
            Intervallen — das kann Stunden dauern. Bis dahin wird die Node-ID angezeigt.
          </p>
          <p className="hint">
            <strong>Positionsgenauigkeit:</strong> Der öffentliche MQTT-Broker gibt nur unpräzise
            Positionen weiter (10–16 Bit, also 3 m bis ~1,2 km). Einstellbar als Kreis um jeden
            Node.
          </p>
          <p className="hint">
            <strong>Kein echter Coverage-Layer:</strong> RSSI-Proben entstehen nur bei Nodes, die
            selbst messen. Die Hexes zeigen deshalb erstmal <em>Node-Dichte</em>. Die Coverage-Hexes
            sind für Phase 2 vorbereitet.
          </p>
        </details>
      </div>

      <div className="sheet-foot">
        <button
          type="button"
          className="link-btn"
          onClick={() =>
            window.open('https://meshtastic.org/docs/software/integrations/mqtt/', '_blank', 'noopener')
          }
        >
          Daten: Meshtastic MQTT
        </button>
      </div>
    </aside>
  );
}